import {
  CreateAccountabilityTemplateRequest,
  UpdateAccountabilityTemplateRequest
} from '../api/accountability-template/accountability-template.types';
import { Checkpoint, PassIndex, TrackedSession } from '../types/common.types';
import {
  CharityActivity,
  CheckpointTemplate,
  Condition,
  OptionalExercise,
  PassDetail,
  PassEntry,
  PassOverview,
  RecommendedExercise,
  SelfCareActivity,
  TrackedSessionDashboard,
  TrackedSessionTemplate
} from '../types/entity.types';

import { convertDateToTime, convertTimeToDate } from './date.util';
import { generateUUID } from './util';

export const initialConditions: Condition[] = [
  { externalReference: '1', optionOne: 'Strong', optionTwo: 'Headache' },
  { externalReference: '2', optionOne: 'Healthy', optionTwo: 'Diarrhea' },
  { externalReference: '3', optionOne: 'Regular', optionTwo: 'Constipated' },
  { externalReference: '4', optionOne: 'Calm Stomach', optionTwo: 'Nauseous' },
  { externalReference: '5', optionOne: 'Energetic', optionTwo: 'Tired' },
  { externalReference: '6', optionOne: 'Full', optionTwo: 'Hungry' },
  { externalReference: '7', optionOne: 'Clear Head', optionTwo: 'Dizzy' },
  { externalReference: '8', optionOne: 'Normal Limbs', optionTwo: 'Tingling' },
  { externalReference: '9', optionOne: 'Comfortable', optionTwo: 'Hot or Cold' },
  { externalReference: '10', optionOne: 'Healthy Joints', optionTwo: 'Joint Pain' },
  { externalReference: '11', optionOne: 'Healthy Skin', optionTwo: 'Itchy' },
  { externalReference: '12', optionOne: 'Confident', optionTwo: 'Anxious' },
  { externalReference: '13', optionOne: 'Calm', optionTwo: 'Irritated' },
  { externalReference: '14', optionOne: 'Happy', optionTwo: 'Depressed' },
  { externalReference: '15', optionOne: 'Clear Memory', optionTwo: 'Fuzzy Memory' },
  { externalReference: '16', optionOne: 'Focused', optionTwo: 'Distracted' },
  { externalReference: '17', optionOne: 'Rested', optionTwo: 'Foggy Brain' }
];

export const initialRecommendedExercises: RecommendedExercise[] = [
  {
    externalReference: generateUUID(),
    name: '',
    measurement: '',
    amounts: [{ externalReference: generateUUID(), fromWeek: '', toWeek: '', amount: '' }]
  }
];

export const initialOptionalExercises: OptionalExercise[] = [
  { externalReference: generateUUID(), name: '', measurement: '' }
];

export const initialSelfCareActivities: SelfCareActivity[] = [{ externalReference: generateUUID(), name: '' }];

export const initialCharityActivities: CharityActivity[] = [
  {
    externalReference: generateUUID(),
    name: '',
    measurement: '',
    amounts: [{ externalReference: generateUUID(), fromWeek: '', toWeek: '', amount: '' }]
  }
];

export const stripEmptyTemplates = (
  input: CreateAccountabilityTemplateRequest | UpdateAccountabilityTemplateRequest
) => {
  const copy = structuredClone(input);

  const { accountabilityTemplate } = copy;

  if (!accountabilityTemplate) return input;

  accountabilityTemplate.charityTemplates = accountabilityTemplate.charityTemplates?.flatMap((template) =>
    template.name ? [template] : []
  );
  accountabilityTemplate.optionalExercise = accountabilityTemplate.optionalExercise!.flatMap((exercise) =>
    exercise.name ? [exercise] : []
  );
  accountabilityTemplate.recommendedExercise = accountabilityTemplate.recommendedExercise!.flatMap((exercise) =>
    exercise.name ? [exercise] : []
  );
  accountabilityTemplate.selfCare = accountabilityTemplate.selfCare!.flatMap((activity) =>
    activity.name ? [activity] : []
  );

  return copy;
};

export const isCheckpointConfigured = (checkpoint: Checkpoint): boolean => {
  if (!checkpoint.name) return false;
  if (!checkpoint.deviceId) return false;
  if (!checkpoint.index) return false;

  if (checkpoint.manualStartTime) {
    return !!checkpoint.startTime;
  }

  if (!checkpoint.basePoints) return false;
  if (!checkpoint.targetTimeInMinutes) return false;
  if (!checkpoint.bonusPoints) return false;

  if (checkpoint.multiplePassesConfigured) {
    if (!checkpoint.multiplePassBasePoints) return false;
    if (!checkpoint.multiplePassCompletedBasePoints) return false;
    if (!checkpoint.multiplePassTargetTimeInMinutes) return false;
    if (!checkpoint.multiplePassBonusPoints) return false;
    if (checkpoint.checkpointPasses.length === 0) return false;
  }

  return true;
};

const mapCheckpointToCheckpointTemplate = (checkpoint: Checkpoint): CheckpointTemplate => ({
  externalReference: checkpoint.isNew ? undefined : checkpoint.uuid,
  name: checkpoint.name,
  deviceId: checkpoint.deviceId,
  index: checkpoint.index ? Number(checkpoint.index) : undefined,
  manualStartTime: checkpoint.manualStartTime,
  basePoints: checkpoint.basePoints ? Number(checkpoint.basePoints) : undefined,
  targetTimeInMinutes: checkpoint.targetTimeInMinutes ? Number(checkpoint.targetTimeInMinutes) : undefined,
  bonusPoints: checkpoint.bonusPoints ? Number(checkpoint.bonusPoints) : undefined,
  multiplePassesConfigured: checkpoint.multiplePassesConfigured,
  checkpointPasses: checkpoint.checkpointPasses,
  multiplePassBasePoints: checkpoint.multiplePassBasePoints ? Number(checkpoint.multiplePassBasePoints) : undefined,
  multiplePassCompletedBasePoints: checkpoint.multiplePassCompletedBasePoints
    ? Number(checkpoint.multiplePassCompletedBasePoints)
    : undefined,
  multiplePassTargetTimeInMinutes: checkpoint.multiplePassTargetTimeInMinutes
    ? Number(checkpoint.multiplePassTargetTimeInMinutes)
    : undefined,
  multiplePassBonusPoints: checkpoint.multiplePassBonusPoints ? Number(checkpoint.multiplePassBonusPoints) : undefined,
  startTime: checkpoint.startTime ? convertTimeToDate(checkpoint.startTime).toISOString() : undefined
});

const mapCheckpointTemplateToCheckpoint = (checkpointTemplate: CheckpointTemplate): Checkpoint => ({
  uuid: checkpointTemplate.externalReference ?? generateUUID(),
  isNew: false,
  name: checkpointTemplate.name,
  deviceId: checkpointTemplate.deviceId,
  index: checkpointTemplate.index?.toString(),
  manualStartTime: checkpointTemplate.manualStartTime,
  basePoints: checkpointTemplate.basePoints?.toString(),
  targetTimeInMinutes: checkpointTemplate.targetTimeInMinutes?.toString(),
  bonusPoints: checkpointTemplate.bonusPoints?.toString(),
  multiplePassesConfigured: checkpointTemplate.multiplePassesConfigured,
  checkpointPasses: checkpointTemplate.checkpointPasses ?? [],
  multiplePassBasePoints: checkpointTemplate.multiplePassBasePoints?.toString(),
  multiplePassCompletedBasePoints: checkpointTemplate.multiplePassCompletedBasePoints?.toString(),
  multiplePassTargetTimeInMinutes: checkpointTemplate.multiplePassTargetTimeInMinutes?.toString(),
  multiplePassBonusPoints: checkpointTemplate.multiplePassBonusPoints?.toString(),
  startTime: checkpointTemplate.startTime ? convertDateToTime(checkpointTemplate.startTime) : undefined
});

export const mapTrackedSessionsToTrackedSessionTemplates = (
  trackedSessions: TrackedSession[]
): TrackedSessionTemplate[] =>
  trackedSessions.map((trackedSession) => ({
    externalReference: trackedSession.isNew ? undefined : trackedSession.uuid,
    name: trackedSession.name,
    startDate: trackedSession.startDate?.toISOString() ?? '',
    startTime: trackedSession.startTime?.toISOString() ?? '',
    endTime: trackedSession.endTime?.toISOString() ?? '',
    checkpointTemplates: trackedSession.checkpoints.map(mapCheckpointToCheckpointTemplate)
  }));

export const mapTrackedSessionTemplatesToTrackedSessions = (
  trackedSessionTemplates: TrackedSessionTemplate[]
): TrackedSession[] =>
  trackedSessionTemplates.map((trackedSessionTemplate) => ({
    uuid: trackedSessionTemplate.externalReference ?? generateUUID(),
    isNew: false,
    name: trackedSessionTemplate.name,
    startDate: new Date(trackedSessionTemplate.startDate),
    startTime: new Date(trackedSessionTemplate.startTime),
    endTime: new Date(trackedSessionTemplate.endTime),
    checkpoints: (trackedSessionTemplate.checkpointTemplates ?? []).map(mapCheckpointTemplateToCheckpoint)
  }));

export const flattenPassIndices = (dashboard: TrackedSessionDashboard): PassIndex[] => {
  const passIndices: PassIndex[] = [];

  dashboard.passOverviews.forEach((passOverview) => {
    if (passOverview.passOverviews.length === 0) {
      passIndices.push({ index: passOverview.index });
    } else {
      passOverview.passOverviews.forEach((subPassOverview) => {
        passIndices.push({ index: passOverview.index, subIndex: subPassOverview.index });
      });
    }
  });

  return passIndices;
};

export const findPassOverviewByIndex = (
  dashboard: TrackedSessionDashboard,
  passIndex: PassIndex
): PassOverview | undefined => {
  const passOverview = dashboard.passOverviews.find((pass) => pass.index === passIndex.index);

  if (passOverview && passIndex.subIndex !== undefined) {
    return passOverview.passOverviews?.find((subPass) => subPass.index === passIndex.subIndex);
  }

  return passOverview;
};

const parseTimeToSeconds = (time: string): number => {
  const [hours = 0, minutes = 0, seconds = 0] = time.split(':').map(Number);
  return hours * 3600 + minutes * 60 + seconds;
};

const formatSecondsToTime = (totalSeconds: number): string =>
  [Math.floor(totalSeconds / 3600), Math.floor((totalSeconds % 3600) / 60), totalSeconds % 60]
    .map((value) => value.toString().padStart(2, '0'))
    .join(':');

export const groupPassDetailByTeam = (passDetail: PassDetail): PassDetail => {
  const entriesByTeam = new Map<string, PassEntry[]>();

  passDetail.leaderboard.forEach((entry) => {
    entry.teamNames.forEach((teamName) => {
      entriesByTeam.set(teamName, [...(entriesByTeam.get(teamName) ?? []), entry]);
    });
  });

  const teamTotals = Array.from(entriesByTeam.entries())
    .map(([teamName, entries]) => ({
      teamName,
      totalSeconds: entries.reduce((total, entry) => total + parseTimeToSeconds(entry.time), 0),
      isManualEntry: entries.some((entry) => entry.isManualEntry)
    }))
    .sort((teamOne, teamTwo) => teamOne.totalSeconds - teamTwo.totalSeconds);

  return {
    leaderboard: teamTotals.map((team, index) => ({
      position: index + 1,
      name: team.teamName,
      teamNames: [],
      time: formatSecondsToTime(team.totalSeconds),
      isManualEntry: team.isManualEntry
    }))
  };
};
